import { $http } from '@/utils/https'

import {
	GET_MY_SUBMISSIONS,
	SAVE_POINT_A,
	SAVE_POINT_B,
	SAVE_AB_FORM_CLOSURE,
	GET_ANALYSIS,
	POLL_ANALYSIS,
	RESET_AB_FORM,
	GET_EXPECTATION_OPTIONS,
} from './types/action-types'

import {
	CHANGE_LOADING,
	CHANGE_SUBMITTING_POINT_A,
	CHANGE_SUBMITTING_POINT_B,
	CHANGE_LOADING_ANALYSIS,
	CHANGE_POLLING_ANALYSIS,
	INCREMENT_CLOSE_ATTEMPTS,
	RESET_CLOSE_ATTEMPTS,
	SET_POINT_A_DATA,
	SET_POINT_B_DATA,
	SET_POINT_A_COMPLETED,
	SET_POINT_B_COMPLETED,
	SET_ANALYSIS,
	SET_ERROR,
	RESET_STATE,
	SET_EXPECTATION_OPTIONS,
	SET_EXPECTATION_LABELS,
} from './types/mutation-types'

const getDefaultPointA = () => ({
	firstName		: '',
	lastName		: '',
	birthDate		: '',
	skillLevel		: 5,
	expectations	: [],
	expectationOther: '',
})

const getDefaultPointB = () => ({
	skillLevelAfter		: 5,
	goalsAchieved		: '',
	goalsExplanation	: '',
	courseRating		: 5,
	recommendationRating: 5,
	difficulties		: [],
	difficultyOther		: '',
	recommendations		: '',
	futureTopics		: '',
	allowContact		: false,
	contactChannel		: '',
	contactInfo			: '',
	contactTime			: [],
})

const getDefaultAnalysis = () => ({
	summary				: null,
	sentiment			: null,
	skillGrowth			: null,
	skillGrowthComment	: null,
	skillBefore			: null,
	skillAfter			: null,
	goalsAchieved		: null,
	courseRating		: null,
	nps					: null,
	aiReady				: false,
	aiError				: null,
	isReady				: false,
})

const state = {
	closeAttempts		: 0,
	maxCloseAttempts	: 5,
	pointA				: getDefaultPointA(),
	pointB				: getDefaultPointB(),
	pointACompleted		: false,
	pointBCompleted		: false,
	analysis			: getDefaultAnalysis(),
	isLoading			: false,
	isSubmittingPointA	: false,
	isSubmittingPointB	: false,
	isLoadingAnalysis	: false,
	isPollingAnalysis	: false,
	error				: null,
	expectationOptions	: [],
	expectationLabels	: {},
}

const getters = {
	canClosePointA			: state => state.closeAttempts < state.maxCloseAttempts,
	remainingCloseAttempts	: state => state.maxCloseAttempts - state.closeAttempts,
	shouldShowWarning		: state => state.closeAttempts === 2,
	pointAData				: state => state.pointA,
	pointBData				: state => state.pointB,
	analysis				: state => state.analysis,
	pointACompleted			: state => state.pointACompleted,
	pointBCompleted			: state => state.pointBCompleted,
	isLoading				: state => state.isLoading,
	isSubmittingPointA		: state => state.isSubmittingPointA,
	isSubmittingPointB		: state => state.isSubmittingPointB,
	isLoadingAnalysis		: state => state.isLoadingAnalysis,
	isPollingAnalysis		: state => state.isPollingAnalysis,
	error					: state => state.error,
	expectationOptions		: state => state.expectationOptions,
	expectationLabels		: state => state.expectationLabels,
	canDownloadCertificate	: state => state.pointBCompleted,
	progressComparison		: state => {
		if (!state.pointACompleted || !state.pointBCompleted) return null
		
		return {
			skillGrowth		: state.pointB.skillLevelAfter - state.pointA.skillLevel,
			startLevel		: state.pointA.skillLevel,
			endLevel		: state.pointB.skillLevelAfter,
			expectations	: state.pointA.expectations,
			goalsAchieved	: state.pointB.goalsAchieved,
		}
	},
}

const mapPointAToApi = (pointA, courseId, packageId) => ({
	course_id			: courseId,
	package_id			: packageId,
	first_name			: pointA.firstName,
	last_name			: pointA.lastName,
	birth_date			: pointA.birthDate,
	skill_level			: pointA.skillLevel,
	expectations		: pointA.expectations,
	expectation_other	: pointA.expectationOther || null,
})

const mapPointBToApi = (pointB, courseId, packageId) => ({
	course_id				: courseId,
	package_id				: packageId,
	skill_level_after		: pointB.skillLevelAfter,
	goals_achieved			: pointB.goalsAchieved,
	goals_explanation		: pointB.goalsExplanation || null,
	course_rating			: pointB.courseRating,
	recommendation_rating	: pointB.recommendationRating,
	difficulties			: pointB.difficulties && pointB.difficulties.length ? pointB.difficulties : [],
	difficulty_other		: pointB.difficultyOther || null,
	recommendations			: pointB.recommendations || null,
	future_topics			: pointB.futureTopics || null,
	allow_contact			: !!pointB.allowContact,
	contact_channel			: pointB.allowContact ? (pointB.contactChannel || null) : null,
	contact_info			: pointB.allowContact ? (pointB.contactInfo || null) : null,
	contact_time			: pointB.allowContact ? (pointB.contactTime || []) : [],
})

/**
 * GET /analysis/{slug} повертає summary, sentiment, skill_growth_comment…
 * GET /my-submissions той самий зміст у вкладеному analysis, але з префіксом ai_* (див. test_ab_form_api.php)
 */
const mapAnalysisFromApi = analysis => {
	const a = analysis || {}
	const str = (...keys) => {
		for (let i = 0; i < keys.length; i++) {
			const v = a[keys[i]]
			if (v != null && String(v).trim() !== '') return v
		}
		return null
	}
	const num = (...keys) => {
		for (let i = 0; i < keys.length; i++) {
			const k = keys[i]
			if (a[k] !== undefined && a[k] !== null) return a[k]
		}
		return null
	}

	return {
		summary				: str('summary', 'ai_summary'),
		sentiment			: str('sentiment', 'ai_sentiment'),
		skillGrowth			: num('skill_growth'),
		skillGrowthComment	: str('skill_growth_comment', 'ai_skill_growth_comment'),
		skillBefore			: num('skill_before'),
		skillAfter			: num('skill_after'),
		goalsAchieved		: a.goals_achieved ? a.goals_achieved : null,
		courseRating		: num('course_rating'),
		nps					: num('nps'),
		aiReady				: !!a.ai_ready,
		aiError				: a.ai_error ? a.ai_error : null,
		isReady				: !!a.is_ready,
	}
}

const mapSubmissionPointAFromApi = pointA => {
	if (!pointA) {
		return null
	}
	
	return {
		id				: pointA.id || null,
		firstName		: pointA.first_name || '',
		lastName		: pointA.last_name || '',
		birthDate		: pointA.birth_date || '',
		skillLevel		: pointA.skill_level || 5,
		expectations	: pointA.expectations || [],
		expectationOther: pointA.expectation_other || '',
		submittedAt		: pointA.submitted_at || null,
	}
}

const mapSubmissionPointBFromApi = pointB => {
	if (!pointB) {
		return null
	}
	
	return {
		id					: pointB.id || null,
		skillLevelAfter		: pointB.skill_level_after || 5,
		goalsAchieved		: pointB.goals_achieved || '',
		goalsExplanation	: pointB.goals_explanation || '',
		courseRating		: pointB.course_rating || 5,
		recommendationRating: pointB.recommendation_rating || 5,
		difficulties		: pointB.difficulties || [],
		difficultyOther		: pointB.difficulty_other || '',
		recommendations		: pointB.recommendations || '',
		futureTopics		: pointB.future_topics || '',
		allowContact		: !!pointB.allow_contact,
		contactChannel		: pointB.contact_channel || '',
		contactInfo			: pointB.contact_info || '',
		contactTime			: pointB.contact_time || [],
		submittedAt			: pointB.submitted_at || null,
	}
}

const actions = {
	[GET_MY_SUBMISSIONS]: async ({ commit, dispatch }, { courseSlug, lang }) => {
		try {
			commit(CHANGE_LOADING, true)
			commit(SET_ERROR, null)

			try {
				await dispatch(GET_EXPECTATION_OPTIONS, { lang: lang || 'uk' })
			} catch (e) {
				// labels optional for progress view
			}
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}course-form/my-submissions/${courseSlug}?use_as_id=slug`
			)
			
			const payload = response.data && response.data.data ? response.data.data : (response.data || {})
			
			const pointA = payload.point_a || null
			const pointB = payload.point_b || null
			
			const analysis = payload.analysis || null
			
			if (pointA) {
				commit(SET_POINT_A_DATA, mapSubmissionPointAFromApi(pointA))
				commit(SET_POINT_A_COMPLETED, true)
			} else {
				commit(SET_POINT_A_DATA, getDefaultPointA())
				commit(SET_POINT_A_COMPLETED, false)
			}
			
			if (pointB) {
				commit(SET_POINT_B_DATA, mapSubmissionPointBFromApi(pointB))
				commit(SET_POINT_B_COMPLETED, true)
			} else {
				commit(SET_POINT_B_DATA, getDefaultPointB())
				commit(SET_POINT_B_COMPLETED, false)
			}
			
			commit(SET_ANALYSIS, mapAnalysisFromApi(analysis || {}))
			
			return response
		} catch (e) {
			commit(SET_ERROR, e.response?.data || e.message)
			throw e
		} finally {
			commit(CHANGE_LOADING, false)
		}
	},
	[SAVE_POINT_A]		: async ({ commit, state }, { courseId, packageId }) => {
		try {
			commit(CHANGE_SUBMITTING_POINT_A, true)
			commit(SET_ERROR, null)
			
			const payload = mapPointAToApi(state.pointA, courseId, packageId)
			
			const response = await $http.post(
				`${process.env.VUE_APP_ROOT_COURSE}course-form/save-point-a`,
				payload
			)
			
			commit(SET_POINT_A_COMPLETED, true)
			commit(RESET_CLOSE_ATTEMPTS)
			
			return response
		} catch (e) {
			commit(SET_ERROR, e.response?.data || e.message)
			throw e
		} finally {
			commit(CHANGE_SUBMITTING_POINT_A, false)
		}
	},
	[SAVE_POINT_B]		: async ({ commit, state, dispatch }, { courseId, packageId, courseSlug, pollAttempts = 10, pollDelay = 3000 }) => {
		try {
			commit(CHANGE_SUBMITTING_POINT_B, true)
			commit(SET_ERROR, null)
			
			const payload = mapPointBToApi(state.pointB, courseId, packageId)
			
			const response = await $http.post(
				`${process.env.VUE_APP_ROOT_COURSE}course-form/save-point-b`,
				payload
			)
			
			commit(SET_POINT_B_COMPLETED, true)
			
			if (courseSlug) {
				await dispatch(POLL_ANALYSIS, {
					courseSlug,
					attempts: pollAttempts,
					delay: pollDelay,
				})
			}
			
			return response
		} catch (e) {
			commit(SET_ERROR, e.response?.data || e.message)
			throw e
		} finally {
			commit(CHANGE_SUBMITTING_POINT_B, false)
		}
	},
	[GET_ANALYSIS]		: async ({ commit }, { courseSlug }) => {
		try {
			commit(CHANGE_LOADING_ANALYSIS, true)
			commit(SET_ERROR, null)
			
			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}course-form/analysis/${courseSlug}?use_as_id=slug`
			)
			
			const payload = response.data && response.data.data ? response.data.data : (response.data || {})
			
			commit(SET_ANALYSIS, mapAnalysisFromApi(payload || {}))
			
			return response
		} catch (e) {
			commit(SET_ERROR, e.response?.data || e.message)
			throw e
		} finally {
			commit(CHANGE_LOADING_ANALYSIS, false)
		}
	},
	[POLL_ANALYSIS]		: async ({ commit, dispatch }, { courseSlug, attempts = 10, delay = 3000 }) => {
		commit(CHANGE_POLLING_ANALYSIS, true)
		commit(SET_ERROR, null)
		
		try {
			let lastResponse = null
			
			for (let i = 0; i < attempts; i++) {
				lastResponse = await dispatch(GET_ANALYSIS, { courseSlug })
				
				const payload = lastResponse.data && lastResponse.data.data	? lastResponse.data.data : (lastResponse.data || {})
				
				if (payload.ai_ready) {
					return {
						success	: true,
						isReady	: true,
						response: lastResponse,
					}
				}
				
				if (payload.ai_error) {
					return {
						success		: false,
						isReady		: false,
						error		: payload.ai_error,
						response	: lastResponse,
					}
				}
				
				if (i < attempts - 1) {
					await new Promise(resolve => setTimeout(resolve, delay))
				}
			}
			
			return {
				success	: false,
				isReady	: false,
				error	: 'AI analysis did not become ready within polling window.',
			}
		} catch (e) {
			commit(SET_ERROR, e.response?.data || e.message)
			throw e
		} finally {
			commit(CHANGE_POLLING_ANALYSIS, false)
		}
	},
	[SAVE_AB_FORM_CLOSURE]	: async ({ commit }, { courseId, packageId, formType }) => {
		try {
			commit(SET_ERROR, null)
			
			const response = await $http.post(
				`${process.env.VUE_APP_ROOT_COURSE}course-form/save-ab-form-closures`,
				{
					course_id	: courseId,
					package_id	: packageId,
					form_type	: formType,
				}
			)
			
			return response
		} catch (e) {
			commit(SET_ERROR, e.response?.data || e.message)
			throw e
		}
	},
	[RESET_AB_FORM]		: ({ commit }) => {
		commit(RESET_STATE)
	},
	[GET_EXPECTATION_OPTIONS]: async ({ commit }, { lang } = {}) => {
		try {
			const params = new URLSearchParams({
				group: 'expectations-learning',
				lang: lang || 'uk',
			})

			const response = await $http.get(
				`${process.env.VUE_APP_ROOT_COURSE}course-form/analysis-options?${params.toString()}`
			)

			const rows = response.data && response.data.data
				? response.data.data
				: (response.data || [])

			const options = rows.map(row => ({
				value: row.key,
				label: row.value,
			}))

			const labels = {}

			options.forEach(option => {
				labels[option.value] = option.label
			})

			labels.other = 'Свій варіант'

			commit(SET_EXPECTATION_OPTIONS, [
				...options,
				{ value: 'other', label: 'Свій варіант' },
			])
			commit(SET_EXPECTATION_LABELS, labels)

			return options
		} catch (e) {
			commit(SET_EXPECTATION_OPTIONS, [
				{ value: 'other', label: 'Свій варіант' },
			])
			commit(SET_EXPECTATION_LABELS, { other: 'Свій варіант' })
			throw e
		}
	},
}

const mutations = {
	[CHANGE_LOADING](state, status) {
		state.isLoading = status
	},
	[CHANGE_SUBMITTING_POINT_A](state, status) {
		state.isSubmittingPointA = status
	},
	[CHANGE_SUBMITTING_POINT_B](state, status) {
		state.isSubmittingPointB = status
	},
	[CHANGE_LOADING_ANALYSIS](state, status) {
		state.isLoadingAnalysis = status
	},
	[CHANGE_POLLING_ANALYSIS](state, status) {
		state.isPollingAnalysis = status
	},
	[INCREMENT_CLOSE_ATTEMPTS](state) {
		state.closeAttempts++
	},
	[RESET_CLOSE_ATTEMPTS](state) {
		state.closeAttempts = 0
	},
	[SET_POINT_A_DATA](state, payload) {
		state.pointA = {
			...state.pointA,
			...payload,
		}
	},
	[SET_POINT_B_DATA](state, payload) {
		state.pointB = {
			...state.pointB,
			...payload,
		}
	},
	[SET_POINT_A_COMPLETED](state, value) {
		state.pointACompleted = value
	},
	[SET_POINT_B_COMPLETED](state, value) {
		state.pointBCompleted = value
	},
	[SET_ANALYSIS](state, analysis) {
		state.analysis = {
			...state.analysis,
			...analysis,
		}
	},
	[SET_ERROR](state, error) {
		state.error = error
	},
	[RESET_STATE](state) {
		state.closeAttempts = 0
		state.pointA = getDefaultPointA()
		state.pointB = getDefaultPointB()
		state.pointACompleted = false
		state.pointBCompleted = false
		state.analysis = getDefaultAnalysis()
		state.isLoading = false
		state.isSubmittingPointA = false
		state.isSubmittingPointB = false
		state.isLoadingAnalysis = false
		state.isPollingAnalysis = false
		state.error = null
		state.expectationOptions = []
		state.expectationLabels = {}
	},
	[SET_EXPECTATION_OPTIONS](state, options) {
		state.expectationOptions = options
	},
	[SET_EXPECTATION_LABELS](state, labels) {
		state.expectationLabels = labels
	},
}

export default {
	namespaced: true,
	state,
	getters,
	actions,
	mutations,
}
